import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import { faQuestion, faSave, faLock, faDownload, faEnvelope, faExclamationTriangle, faArrowLeft, faCheck, faExternalLinkAlt, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { IError } from 'src/app/models/error.model';
import { IIncidentAnswer, IIncidentQuestion, IIncidentQuestionOption, IIncidentSection } from 'src/app/models/incident.model';
import { IAttachment } from 'src/app/models/attachment.model';
import { ITreatmentPlanDetailed } from 'src/app/models/treatment-plan.model';
import { IncidentService } from 'src/app/services/incident.service';
import { AttachmentService } from 'src/app/services/attachment.service';
import { TreatmentPlanService } from 'src/app/services/treatment-plan.service';
import { ProductService, IApplicationRegionCategory } from 'src/app/services/product.service';
import { IRegionSelectorConfig, IncidentFormComponent } from 'src/app/components/incident-form/incident-form.component';

type SaveIndicatorState = 'idle' | 'saving' | 'saved' | 'error' | 'unsaved';

@Component({
  selector: 'app-incident-detail-v2',
  templateUrl: './incident-detail.component.html'
})
export class IncidentDetailV2Component implements OnInit, OnDestroy {
  @ViewChild(IncidentFormComponent) incidentForm?: IncidentFormComponent;

  // Ícones
  faQuestion = faQuestion;
  faSave = faSave;
  faLock = faLock;
  faDownload = faDownload;
  faEnvelope = faEnvelope;
  faExclamationTriangle = faExclamationTriangle;
  faArrowLeft = faArrowLeft;
  faCheck = faCheck;
  faExternalLinkAlt = faExternalLinkAlt;
  faPaperPlane = faPaperPlane;

  // Estado de carregamento
  loading = true;
  loadError = false;

  // Plano e intercorrência
  treatmentPlanId = '';
  treatmentPlan: ITreatmentPlanDetailed | null = null;
  incidentId: string | null = null;
  incidentStatus: 'ACTIVE' | 'CLOSED' | 'DELETED' = 'ACTIVE';

  // Estrutura do formulário
  incidentSections: IIncidentSection[] = [];
  hasClinicQuestions = false;
  incidentFixedValues: Record<string, string> = {};
  incidentDynamicOptions: Record<string, string[]> = {};
  incidentRegionSelectorConfig: Record<string, IRegionSelectorConfig> = {};
  existingAnswers: IIncidentAnswer[] = [];
  existingAttachments: IAttachment[] = [];

  // Dados da assinatura (Autentique) — preenchidos quando o relatório está CLOSED
  autentiqueSigningLink: string | null = null;
  autentiqueDocumentId: string | null = null;
  signedPdfKey: string | null = null;

  // Regiões de aplicação (faciais/corporais)
  private facialRegionsFromApi: string[] = [];
  private corporalRegionsFromApi: string[] = [];
  private applicationRegionCategories: IApplicationRegionCategory[] = [];

  // Estado de save/finalize
  saveState: SaveIndicatorState = 'idle';
  lastSavedAt: Date | null = null;
  saving = false;
  finalizing = false;

  // UI
  showHelpInfo = false;
  showFinalizeConfirm = false;
  /** Modal exibido quando o plano ainda não tem intercorrência — pede confirmação antes de criar. */
  showCreateConfirm = false;
  /** Em andamento criando a intercorrência (após confirmação do usuário). */
  creating = false;

  // Auto-save: marca primeira mudança após carregamento para evitar PUT redundante
  private firstChangeIgnored = false;
  /** Guarda contra múltiplas execuções de bootstrap (defesa contra re-init do componente). */
  private bootstrapStarted = false;

  /**
   * Auto-save single-flight:
   * - `autoSaveInFlight` indica se já tem um PUT pendente.
   * - `pendingAutoSavePayload` guarda a última versão do payload caso uma nova
   *   alteração chegue enquanto outra requisição está em flight. Quando o PUT
   *   atual terminar, o coalesced payload é disparado.
   * Sem isso, dois PUTs simultâneos racham na concorrência (StaleStateException
   * no backend mesmo com lock pessimista — o lock só serializa, não evita o erro
   * se a primeira req falhar e a segunda já ter passado por findById com estado
   * fresh).
   */
  private autoSaveInFlight = false;
  private pendingAutoSavePayload: { answers: IIncidentAnswer[]; attachments: IAttachment[] } | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private store: Store<{ warningPopup: IError }>,
    private incidentService: IncidentService,
    private attachmentService: AttachmentService,
    private treatmentPlanService: TreatmentPlanService,
    private productService: ProductService
  ) {}

  ngOnInit(): void {
    this.treatmentPlanId = this.route.snapshot.paramMap.get('id') || '';
    if (!this.treatmentPlanId) {
      this.loadError = true;
      this.loading = false;
      return;
    }

    this.bootstrap();
  }

  ngOnDestroy(): void {
    // nada por enquanto — auto-save é debounced, descarta naturalmente.
  }

  // ── Bootstrap ───────────────────────────────────────────────────────

  private async bootstrap(): Promise<void> {
    // Guarda contra execução dupla — evita disparar o conjunto de GETs duas vezes
    // se ngOnInit for chamado de novo por algum efeito do roteador.
    if (this.bootstrapStarted) {
      return;
    }
    this.bootstrapStarted = true;
    this.loading = true;
    this.loadError = false;

    try {
      // 1. Carregar plano + regiões em paralelo
      const [plan, regions] = await Promise.all([
        this.treatmentPlanService.getTreatmentPlanById(this.treatmentPlanId),
        this.loadApplicationRegions()
      ]);
      this.treatmentPlan = plan;
      this.applicationRegionCategories = regions;
      this.facialRegionsFromApi = this.extractRegionsByCategory(regions, 'Facial');
      this.corporalRegionsFromApi = this.extractRegionsByCategory(regions, 'Corporal');

      // 2. Carregar perguntas da clínica e relatório existente em paralelo
      const [clinicQuestions, existingReports] = await Promise.all([
        this.loadClinicQuestionsSafely(),
        this.incidentService.getIncidentReports(this.treatmentPlanId)
      ]);

      // 3. Montar seções (fixa + clínica)
      this.buildSections(clinicQuestions);
      this.buildFixedValuesAndRegions();

      // 4. Se já existir relatório, carrega.
      //    Se NÃO existir, abre modal pedindo confirmação para criar — não cria automaticamente.
      if (existingReports && existingReports.length > 0) {
        const report = existingReports[0];
        this.applyExistingReport(report);
      } else if (this.treatmentPlan?.status === 'ACTIVE') {
        // Plano ativo, sem intercorrência → solicitar criação explícita
        this.showCreateConfirm = true;
      }
      // Plano CLOSED sem intercorrência → estado vazio com aviso (mostrado pelo template).
    } catch (err) {
      console.error('[IncidentDetail] Falha ao carregar:', err);
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  private async loadApplicationRegions(): Promise<IApplicationRegionCategory[]> {
    return new Promise(resolve => {
      this.productService.getApplicationRegions().subscribe({
        next: (data) => resolve(data || []),
        error: () => resolve([])
      });
    });
  }

  private extractRegionsByCategory(categories: IApplicationRegionCategory[], category: string): string[] {
    const cat = categories.find(c => c.category === category);
    return cat ? cat.regions.map(r => r.value) : [];
  }

  private async loadClinicQuestionsSafely(): Promise<any> {
    try {
      return await this.incidentService.getLatestQuestions();
    } catch (err: any) {
      console.warn('[IncidentDetail] Sem perguntas configuradas (OK):', err?.status);
      return null;
    }
  }

  private buildSections(clinicQuestions: any): void {
    // Seção fixa: 3 perguntas no topo
    const procedureOptions: IIncidentQuestionOption[] = this.buildProcedureOptions();

    const fixedSection: IIncidentSection = {
      id: '__fixed_section__',
      section_title: 'Informações do Procedimento',
      section_order: 0,
      questions: [
        {
          id: '__fixed_data_procedimento__',
          question_text: 'Data do procedimento',
          type: 'DATE',
          is_required: true,
          placeholder: '',
          order_in_section: 1,
          options: []
        },
        {
          id: '__fixed_procedimento__',
          question_text: 'Procedimento da intercorrência',
          type: 'MULTIPLE_CHOICE',
          is_required: true,
          placeholder: '',
          order_in_section: 2,
          options: procedureOptions
        },
        {
          id: '__fixed_area_tratada__',
          question_text: 'Área tratada',
          type: 'TEXT_INPUT',
          is_required: true,
          placeholder: '',
          order_in_section: 3,
          options: []
        }
      ]
    };

    // Perguntas configuráveis da clínica
    const clinicSections: IIncidentSection[] = [];
    const latestSections: any[] = Array.isArray(clinicQuestions?.sections) ? clinicQuestions.sections : [];

    for (const [idx, ls] of latestSections.entries()) {
      clinicSections.push({
        id: ls.section_id || ls.id || '',
        section_title: ls.section_name || ls.section_title || '',
        section_order: (ls.section_order || (idx + 1)) + 1,
        questions: (Array.isArray(ls.questions) ? ls.questions : []).map((q: any) => this.mapQuestion(q))
      });
    }

    this.incidentSections = [fixedSection, ...clinicSections];
    this.hasClinicQuestions = clinicSections.length > 0;
  }

  private mapQuestion(q: any): IIncidentQuestion {
    return {
      id: q.id || '',
      question_text: q.label ?? q.question_text ?? '',
      type: q.type || 'TEXT_INPUT',
      is_required: q.is_required ?? false,
      placeholder: q.placeholder ?? '',
      order_in_section: q.question_order ?? q.order_in_section ?? 0,
      options: (Array.isArray(q.options) ? q.options : []).map((o: any) => ({
        id: o.id || '',
        option_text: o.option_label ?? o.option_text ?? '',
        option_order: o.option_order ?? 0,
        triggers_free_text: o.triggers_free_text ?? false,
        sub_questions: (Array.isArray(o.sub_questions) ? o.sub_questions : []).map((sq: any) => this.mapQuestion(sq))
      })),
      object_fields: (Array.isArray(q.object_fields) ? q.object_fields : []).map((f: any) => ({
        id: f.id || '',
        field_key: f.field_key || f.fieldKey || '',
        field_label: f.field_label || f.fieldLabel || '',
        field_type: f.field_type || f.fieldType || 'TEXT',
        placeholder: f.placeholder || '',
        is_required: f.is_required ?? f.isRequired ?? false,
        field_order: f.field_order ?? f.fieldOrder ?? 0,
        select_options: Array.isArray(f.select_options ?? f.selectOptions) ? (f.select_options ?? f.selectOptions) : []
      }))
    };
  }

  private buildProcedureOptions(): IIncidentQuestionOption[] {
    if (!this.treatmentPlan?.contracted_procedures) return [];
    const raw = this.treatmentPlan.contracted_procedures;
    const procedures = raw
      .split(/,\s*(?=[A-ZÀ-ÚÇ])/)
      .flatMap((part: string) => part.split(/\s+e\s+(?=[A-ZÀ-ÚÇ])/))
      .map((p: string) => p.trim())
      .filter((p: string) => p.length > 0);
    return procedures.map((proc, idx) => ({
      id: proc,
      option_text: proc,
      option_order: idx + 1,
      triggers_free_text: false
    }));
  }

  private buildFixedValuesAndRegions(): void {
    // Data do procedimento — pré-preencher com created_at do plano
    let dateValue = '';
    const createdAt = this.treatmentPlan?.created_at;
    if (createdAt) {
      if (createdAt.includes('T')) {
        dateValue = createdAt.split('T')[0];
      } else if (createdAt.includes('/')) {
        const parts = createdAt.split('/');
        if (parts.length === 3) {
          dateValue = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      } else if (/^\d{4}-\d{2}-\d{2}/.test(createdAt)) {
        dateValue = createdAt.substring(0, 10);
      }
    }
    this.incidentFixedValues = dateValue ? { 'Data do procedimento': dateValue } : {};

    // Área tratada — pré-selecionar regiões dos produtos
    const allProductRegions: string[] = [];
    if (this.treatmentPlan?.products && this.treatmentPlan.products.length > 0) {
      for (const product of this.treatmentPlan.products) {
        if (product.application_regions && product.application_regions.length > 0) {
          for (const region of product.application_regions) {
            const site = region.application_site;
            if (site && !allProductRegions.includes(site)) {
              allProductRegions.push(site);
            }
          }
        }
      }
    }

    this.incidentRegionSelectorConfig = {
      'Área tratada': {
        facial: this.facialRegionsFromApi,
        corporal: this.corporalRegionsFromApi,
        preSelected: allProductRegions
      }
    };

    this.incidentDynamicOptions = {};
  }

  private applyExistingReport(report: any): void {
    this.incidentId = report.id || null;
    this.incidentStatus = report.status || 'ACTIVE';
    this.existingAnswers = this.extractAnswersFromReport(report);
    this.existingAttachments = report.attachments || [];
    this.lastSavedAt = report.updated_at ? new Date(report.updated_at) : null;
    this.saveState = 'saved';

    // Dados de assinatura — disponíveis após finalização
    this.autentiqueSigningLink = report.autentique_signing_link || null;
    this.autentiqueDocumentId = report.autentique_document_id || null;
    this.signedPdfKey = report.signed_pdf_s3_key || null;
  }

  /**
   * Estado atual da assinatura (somente para relatórios CLOSED).
   * O backend persiste signed_pdf_s3_key quando a Autentique confirma a assinatura
   * via webhook — usamos isso como sinal de "assinado".
   */
  get signatureStatus(): 'pending' | 'signed' | 'none' {
    if (this.incidentStatus !== 'CLOSED') return 'none';
    if (this.signedPdfKey) return 'signed';
    return 'pending';
  }

  get signatureStatusText(): string {
    return this.signatureStatus === 'signed' ? 'Assinado' : 'Aguardando assinatura';
  }

  openSigningLink(): void {
    if (this.autentiqueSigningLink) {
      window.open(this.autentiqueSigningLink, '_blank', 'noopener,noreferrer');
    } else if (this.autentiqueDocumentId) {
      window.open(`https://painel.autentique.com.br/documentos/${this.autentiqueDocumentId}`, '_blank', 'noopener,noreferrer');
    } else {
      this.store.dispatch(setWarningText('Link de assinatura indisponível. Tente novamente em instantes.', 500));
    }
  }

  /**
   * Confirma a criação da intercorrência (chamado pelo botão "Criar" do modal).
   * Só é executado após confirmação explícita do usuário — nunca automaticamente.
   */
  async confirmCreateIncident(): Promise<void> {
    if (this.creating) return;
    if (this.treatmentPlan?.status !== 'ACTIVE') {
      this.showCreateConfirm = false;
      return;
    }

    this.creating = true;
    this.saveState = 'saving';
    try {
      const created = await this.incidentService.saveIncidentReport(this.treatmentPlanId, {
        answers: [],
        attachments: []
      } as any);
      this.applyExistingReport(created);
      this.showCreateConfirm = false;
    } catch (err) {
      console.error('[IncidentDetail] Falha ao criar intercorrência:', err);
      this.saveState = 'error';
      this.store.dispatch(setWarningText('Não foi possível criar a intercorrência. Tente novamente.', 500));
    } finally {
      this.creating = false;
    }
  }

  /** Cancela a criação — usuário volta para a tela do plano. */
  cancelCreateIncident(): void {
    this.showCreateConfirm = false;
    this.goBack();
  }

  // ── Conversão de respostas (replicada do treatment-plan.component) ───

  private extractAnswersFromReport(report: any): IIncidentAnswer[] {
    const answers: IIncidentAnswer[] = [];
    const seenIds = new Set<string>();

    if (Array.isArray(report.answers)) {
      for (const a of report.answers) {
        const qid = a.question_id || a.questionId;
        if (!qid || seenIds.has(qid)) continue;
        seenIds.add(qid);
        answers.push({
          question_id: qid,
          answer: a.answer ?? '',
          observation: a.observation ?? '',
          object_entries: a.object_entries ?? a.objectEntries ?? []
        });
      }
    }
    return answers;
  }

  // ── Auto-save ────────────────────────────────────────────────────────

  /**
   * Recebe formChange (debounced) do componente filho.
   *
   * Single-flight: se já tem um PUT em flight, guarda o payload mais recente em
   * `pendingAutoSavePayload`. Quando o PUT atual terminar, dispara o pendente.
   * Isso garante que o backend nunca recebe dois PUTs concorrentes do auto-save
   * (que disparariam race conditions no clear/persist do incident_answer).
   */
  onFormChange(payload: { answers: IIncidentAnswer[]; attachments: IAttachment[] }): void {
    if (!this.incidentId || this.incidentStatus === 'CLOSED') return;

    if (this.autoSaveInFlight) {
      // PUT já em flight — apenas atualiza o payload pendente (overwrite, mais recente vence).
      this.pendingAutoSavePayload = payload;
      return;
    }

    void this.runAutoSave(payload);
  }

  private async runAutoSave(payload: { answers: IIncidentAnswer[]; attachments: IAttachment[] }): Promise<void> {
    this.autoSaveInFlight = true;
    this.saveState = 'saving';
    try {
      await this.incidentService.updateIncidentReport(this.treatmentPlanId, this.incidentId!, {
        answers: payload.answers
      } as any);

      // Registrar anexos novos (sem id)
      await this.persistNewAttachments(payload.attachments);

      this.lastSavedAt = new Date();
      this.saveState = 'saved';
    } catch (err) {
      console.error('[IncidentDetail] Falha no auto-save:', err);
      this.saveState = 'error';
    } finally {
      this.autoSaveInFlight = false;
      // Se chegou novo payload durante o PUT, dispara agora (coalescing).
      if (this.pendingAutoSavePayload) {
        const next = this.pendingAutoSavePayload;
        this.pendingAutoSavePayload = null;
        void this.runAutoSave(next);
      }
    }
  }

  private async persistNewAttachments(attachments: IAttachment[]): Promise<void> {
    if (!attachments || attachments.length === 0) return;
    for (const [idx, att] of attachments.entries()) {
      if (!att.id) {
        const data = {
          file_name: att.file_name,
          file_type: (att as any).content_type || (att as any).file_type || '',
          file_size: att.file_size,
          storage_key: att.storage_key,
          attachment_order: idx
        };
        try {
          await this.attachmentService.registerAttachment(this.treatmentPlanId, data);
        } catch (err) {
          console.error('[IncidentDetail] Erro ao registrar anexo:', err);
        }
      }
    }
  }

  // ── Save manual ──────────────────────────────────────────────────────

  async onSaveClick(): Promise<void> {
    if (!this.incidentForm || !this.incidentId) return;
    if (this.saving || this.finalizing) return;

    // Aguarda qualquer auto-save em andamento terminar antes de salvar manualmente.
    // Sem isso, save manual + auto-save concorrentes geram race condition no backend.
    await this.waitForAutoSaveIdle();

    this.saving = true;
    this.saveState = 'saving';
    try {
      const payload = this.incidentForm.getPayload();
      await this.incidentService.updateIncidentReport(this.treatmentPlanId, this.incidentId, {
        answers: payload.answers
      } as any);
      await this.persistNewAttachments(payload.attachments);

      // Recarregar dados (anexos podem ter recebido id após o registro)
      await this.reloadReport();

      this.lastSavedAt = new Date();
      this.saveState = 'saved';
      this.store.dispatch(setWarningText('Intercorrência salva com sucesso.', 200));
    } catch (err) {
      console.error('[IncidentDetail] Falha ao salvar:', err);
      this.saveState = 'error';
      this.store.dispatch(setWarningText('Erro ao salvar intercorrência. Tente novamente.', 500));
    } finally {
      this.saving = false;
    }
  }

  /**
   * Aguarda o término de qualquer auto-save em andamento (incluindo coalescing
   * de payloads pendentes). Garante que ações manuais (Salvar/Finalizar) não
   * concorram com o PUT do auto-save.
   */
  private async waitForAutoSaveIdle(): Promise<void> {
    const start = Date.now();
    while ((this.autoSaveInFlight || this.pendingAutoSavePayload) && Date.now() - start < 10000) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  private async reloadReport(): Promise<void> {
    const reports = await this.incidentService.getIncidentReports(this.treatmentPlanId);
    if (reports && reports.length > 0) {
      this.applyExistingReport(reports[0]);
    }
  }

  // ── Finalizar ────────────────────────────────────────────────────────

  onFinalizeClick(): void {
    if (!this.incidentForm) return;
    if (this.saving || this.finalizing) return;

    if (!this.incidentForm.validateForFinalize()) {
      this.store.dispatch(setWarningText('Preencha os campos obrigatórios antes de finalizar.', 500));
      return;
    }
    this.showFinalizeConfirm = true;
  }

  cancelFinalize(): void {
    this.showFinalizeConfirm = false;
  }

  async confirmFinalize(): Promise<void> {
    if (!this.incidentForm || !this.incidentId) return;
    this.showFinalizeConfirm = false;
    this.finalizing = true;
    this.saveState = 'saving';

    try {
      // Aguarda qualquer auto-save em andamento — finalize não pode concorrer
      // com PUT do auto-save (mesma fila do clear/persist no backend).
      await this.waitForAutoSaveIdle();

      // Save antes de finalizar para garantir que respostas mais recentes estão persistidas
      const payload = this.incidentForm.getPayload();
      await this.incidentService.updateIncidentReport(this.treatmentPlanId, this.incidentId, {
        answers: payload.answers
      } as any);
      await this.persistNewAttachments(payload.attachments);

      // Finalizar
      await this.incidentService.finalizeIncidentReport(this.treatmentPlanId);
      await this.reloadReport();

      this.store.dispatch(setWarningText('Intercorrência finalizada com sucesso.', 200));
    } catch (err) {
      console.error('[IncidentDetail] Falha ao finalizar:', err);
      this.saveState = 'error';
      this.store.dispatch(setWarningText('Erro ao finalizar intercorrência. Tente novamente.', 500));
    } finally {
      this.finalizing = false;
    }
  }

  // ── Navegação ────────────────────────────────────────────────────────

  goBack(): void {
    this.router.navigate(['/main/v2/treatment-plan', this.treatmentPlanId]);
  }

  // ── Help ─────────────────────────────────────────────────────────────

  toggleHelp(): void {
    this.showHelpInfo = !this.showHelpInfo;
  }

  // ── Status helpers ───────────────────────────────────────────────────

  get isReadOnly(): boolean {
    return this.treatmentPlan?.status === 'CLOSED' || this.incidentStatus === 'CLOSED';
  }

  get statusBadgeText(): string {
    if (this.incidentStatus === 'CLOSED') return 'Finalizada';
    return 'Em rascunho';
  }

  get statusBadgeClass(): string {
    if (this.incidentStatus === 'CLOSED') return 'bg-green-100 text-green-800';
    return 'bg-westetics-purple-100 text-westetics-purple-800';
  }

  get saveIndicatorText(): string {
    switch (this.saveState) {
      case 'saving': return 'Salvando…';
      case 'saved':
        return this.lastSavedAt
          ? `Salvo às ${this.lastSavedAt.getHours().toString().padStart(2, '0')}:${this.lastSavedAt.getMinutes().toString().padStart(2, '0')}`
          : 'Salvo';
      case 'unsaved': return 'Alterações não salvas';
      case 'error': return 'Erro ao salvar';
      default: return '';
    }
  }
}
